import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const errorContainer = payload as ApiErrorPayload;
    const message = errorContainer.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

interface UserRecord {
  id: number | string;
  name: string;
}

function isUserRecord(value: unknown): value is UserRecord {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as { id?: unknown; name?: unknown };
  const hasValidId = typeof candidate.id === 'number' || typeof candidate.id === 'string';
  return hasValidId && typeof candidate.name === 'string';
}

export interface Employee {
  id: string;
  name: string;
}

async function fetchEmployees(): Promise<Employee[]> {
  const response = await apiFetch('/api/v1/users');
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(
      payload,
      `Failed to fetch users: ${response.status} ${response.statusText}`
    );
    throw new Error(message);
  }

  if (!Array.isArray(payload)) {
    return [];
  }

  return payload.filter(isUserRecord).map((item) => ({
    id: String(item.id),
    name: item.name,
  }));
}

export function useEmployees() {
  return useQuery({
    queryKey: queryKeys.employees,
    queryFn: fetchEmployees,
    staleTime: 1000 * 60 * 5,
  });
}
